import {
  DatabaseAuto, IdataArrayResponse, Ifile, IfilterProps, Iuser, TuserRequest, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AuthClient } from '../auth-client';

/**
 * Alias for the User data transfer object as returned by the Auth service.
 *
 * @public
 */
export type TuserProps = Iuser;

/**
 * Client-side model representing an authenticated user and exposing
 * helper methods to query, filter, update, and upload profile media.
 *
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link User} class when invoking client operations.
 *
 * @example
 * ```typescript
 * // Get all users with pagination
 * const { count, dataArr: users } = await User.getAll(0, 10);
 *
 * // Filter users by criteria
 * const filteredUsers = await User.filterAll({
 *   where: {
 *     email: { $regex: 'example.com', $options: 'i' },
 *     verified: true
 *   },
 *   sort: { createdAt: -1 }
 * });
 *
 * // Get a single user by ID
 * const user = await User.getOne('user123');
 *
 * // Update user profile
 * const updatedUser = await user.updateProfile({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   phone: '+1234567890'
 * });
 *
 * // Upload profile picture
 * const fileInput = document.querySelector('input[type="file"]');
 * const file = fileInput.files[0];
 * await user.uploadProfilePicture(file);
 *
 * // Delete user images
 * await user.deleteImages(['image123', 'image456']);
 *
 * // Static method to update a user by ID
 * const updatedUser = await User.updateOne('user123', {
 *   firstName: 'Jane',
 *   lastName: 'Smith'
 * });
 *
 * // Delete a user by ID
 * await User.remove('user123');
 *
 * ```
 */

export class User extends DatabaseAuto {
  private static readonly baseUrl = '/user';
  readonly urId: TuserProps['urId'];
  readonly firstName: TuserProps['firstName'];
  readonly lastName: TuserProps['lastName'];
  readonly email: TuserProps['email'];
  readonly profilePicture?: TuserProps['profilePicture'];
  readonly profileCoverPicture?: TuserProps['profileCoverPicture'];
  readonly phone: TuserProps['phone'];
  readonly online: TuserProps['online'];
  readonly verified?: TuserProps['verified'];
  readonly socialAuthFrameworks?: TuserProps['socialAuthFrameworks'];
  readonly phoneVerificationMeta?: TuserProps['phoneVerificationMeta'];
  readonly emailVerificationMeta?: TuserProps['emailVerificationMeta'];
  readonly recoveryPhoneNumbers?: TuserProps['recoveryPhoneNumbers'];
  readonly recoveryEmails?: TuserProps['recoveryEmails'];
  readonly phoneCode?: TuserProps['phoneCode'];
  readonly twoFaConfig?: TuserProps['twoFaConfig'];
  readonly preferredLoginMethod?: TuserProps['preferredLoginMethod'];

  /**
   * Constructs a {@link User} instance from provided data after basic validation.
   *
   * Ensures at least one contact method exists and validates common fields.
   *
   * @param data - The user data to initialize the model with.
   * @throws {@link BizNestClientValidationError} If required fields are invalid.
   */
  constructor(data: TuserProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'User ID must be a non-empty string'
      },
      email: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)),
        message: 'Invalid email format'
      },
      phone: {
        required: false,
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Phone must be a non-empty string'
      },
      verified: {
        required: false,
        validate: (v) => typeof v === 'boolean',
        message: 'Verified must be a boolean'
      }
    });

    super(data);

    this.urId = data.urId;

    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.email = data.email;
    this.profilePicture = data.profilePicture;
    this.profileCoverPicture = data.profileCoverPicture;
    this.phone = data.phone;
    this.verified = data.verified || false;
    this.socialAuthFrameworks = data.socialAuthFrameworks;
    this.phoneVerificationMeta = data.phoneVerificationMeta;
    this.emailVerificationMeta = data.emailVerificationMeta;
    this.recoveryPhoneNumbers = data.recoveryPhoneNumbers;
    this.recoveryEmails = data.recoveryEmails;
    this.phoneCode = data.phoneCode;
    this.twoFaConfig = data.twoFaConfig;
    this.preferredLoginMethod = data.preferredLoginMethod;
  }

  /**
   * The user's displayable name (first and last concatenated).
   */
  get names() {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  /**
   * The user's full display name (alias of {@link User.names}).
   */
  get fullNames() {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  /**
   * Retrieves all users with pagination.
   *
   * @param offset - The starting point for the list of users. Defaults to `0`.
   * @param limit - The maximum number of users to retrieve. Defaults to `20`.
   * @returns A promise that resolves to an object containing the count and an
   * array of {@link User} instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = AuthClient.ehttp
      .makeGet<IdataArrayResponse<TuserProps>>(`${User.baseUrl}?${query}`);
    const users = await lastValueFrom(observer$);

    return {
      count: users.count,
      dataArr: users.data.map(val => new User(val))
    };
  }

  /**
   * Filters users based on provided criteria.
   *
   * @param filter - The filter criteria for retrieving users.
   * @returns A promise that resolves to an object containing the count and an
   * array of {@link User} instances.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = AuthClient.ehttp.makePost<IdataArrayResponse<TuserProps>>(`${User.baseUrl}/filter`, filter);
    const users = await lastValueFrom(observer$);

    return {
      count: users.count,
      dataArr: users.data.map(val => new User(val))
    };
  }

  /**
   * Retrieves a single user by ID.
   *
   * @param resourceId - The unique identifier of the user.
   * @returns A promise that resolves to the {@link User} instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = AuthClient.ehttp.makeGet<TuserProps>(`${User.baseUrl}/${resourceId}`);

    return new User(await lastValueFrom(observer$));
  }

  /**
   * Updates a user by identifier with the provided payload.
   *
   * @param _id - The user identifier to update.
   * @param payload - The update request body.
   * @returns A promise that resolves to the updated {@link User} instance.
   */
  static async updateOne(_id: string, payload: TuserRequest) {
    const observer$ = AuthClient.ehttp
      .makePut<TuserProps>(`${User.baseUrl}/${_id}`, payload);

    return new User(await lastValueFrom(observer$));
  }

  /**
   * Updates the current user's profile with the provided payload.
   *
   * @param payload - The update request body.
   * @returns A promise that resolves to the updated {@link User} instance.
   */
  async updateProfile(payload: TuserRequest) {
    const observer$ = AuthClient.ehttp
      .makePut<TuserProps>(`${User.baseUrl}/${this._id}`, payload);

    return new User(await lastValueFrom(observer$));
  }


  /**
   * Uploads a new profile picture for the current user instance.
   *
   * @param file - The image file to upload.
   * @returns An observable that emits the updated user DTO.
   */
  uploadProfilePicture(file: Ifile) {
    return AuthClient.ehttp
      .uploadFiles<TuserProps>([file], `${User.baseUrl}/upload-profile-picture`, { _id: this._id });
  }

  /**
   * Uploads a new profile cover picture for the current user instance.
   *
   * @param file - The image file to upload.
   * @returns An observable that emits the updated user DTO.
   */
  uploadProfileCoverPicture(file: Ifile) {
    return AuthClient.ehttp
      .uploadFiles<TuserProps>([file], `${User.baseUrl}/upload-profile-cover-picture`, { _id: this._id });
  }

  /**
   * Removes images associated with the current user instance.
   *
   * @param fileIds - The image identifiers to remove.
   * @returns A promise that resolves to the updated {@link User} instance.
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = AuthClient.ehttp
      .makePost<TuserProps>(`${User.baseUrl}/delete-images`, { fileIds, _id: this._id });


    return new User(await lastValueFrom(observer$));
  }
}
